import type { GstMode } from "@luckymenu/core";
import { allocate, type DiscountInput, discountAmount } from "./discount";
import { assertBasisPoints, assertInteger, assertPaise, mulDivRound, sum } from "./integer";
import { lineGross } from "./pricing";
import { type GroupTax, taxInInclusive, taxOnExclusive } from "./tax";

/**
 * Bill calculation, as specified in docs/architecture/ordering-billing-gst.md
 * under "Bill calculation". The server's result is final; clients use the same
 * function for previews.
 *
 * Order of calculation:
 * 1. Line gross = (unit price + modifiers) × quantity.
 * 2. The item discount is taken off each line.
 * 3. The bill discount is spread over the lines in proportion to their value
 *    after item discounts (largest remainder method, see `allocate`).
 * 4. Service charge = round(discounted subtotal × rate ÷ 10000). It is one more
 *    line at the restaurant's default tax rate, and follows the same inclusive
 *    or exclusive setting as the prices.
 * 5. Tax is computed once per tax rate group, never per line (see tax.ts).
 *    Only in the regular GST mode. In `none` and `composition` there is no tax
 *    and prices are taken as they are, whether or not they say "inclusive".
 * 6. Round-off, when on, brings the total to the nearest rupee (50 paise up).
 * 7. Total = taxable + CGST + SGST + round-off.
 *
 * Every amount is integer paise and every rate integer basis points.
 */

export interface BillLineModifier {
	name: string;
	priceDeltaPaise: number;
}

/** One active order item, as snapshotted on the order. */
export interface BillLineInput {
	/** The order item. Echoed so callers can match results to rows. */
	id: string;
	name: string;
	variantName: string | null;
	modifiers: readonly BillLineModifier[];
	unitPricePaise: number;
	/** Sum of `modifiers` deltas for one unit. */
	modifiersTotalPaise: number;
	quantity: number;
	/** Tax rate at the time of sale. Used only in the regular GST mode. */
	taxRateBp: number;
	discount: DiscountInput | null;
}

export interface BillInput {
	gstMode: GstMode;
	pricesIncludeTax: boolean;
	/** The rate of the restaurant's default tax class. Taxes the service charge. */
	defaultTaxRateBp: number;
	/** `service_charge_bp`. 0 for none. */
	serviceChargeBp: number;
	roundOff: boolean;
	lines: readonly BillLineInput[];
	billDiscount: DiscountInput | null;
}

export interface BillLine {
	id: string;
	name: string;
	variantName: string | null;
	modifiers: BillLineModifier[];
	unitPricePaise: number;
	modifiersTotalPaise: number;
	quantity: number;
	taxRateBp: number;
	/** (unit price + modifiers) × quantity. */
	grossPaise: number;
	discount: DiscountInput | null;
	itemDiscountPaise: number;
	/** This line's share of the bill discount. */
	billDiscountPaise: number;
	/** gross − item discount − bill discount share. Includes tax when prices do. */
	netPaise: number;
}

export interface AppliedDiscount extends DiscountInput {
	amountPaise: number;
}

export interface ServiceCharge {
	rateBp: number;
	/** On the discounted subtotal. Includes its tax when prices include tax. */
	amountPaise: number;
	/** The default rate in the regular GST mode, otherwise 0. */
	taxRateBp: number;
}

export interface TaxGroup extends GroupTax {
	rateBp: number;
}

export interface BillTotals {
	/** Sum of line gross. */
	subtotalPaise: number;
	itemDiscountPaise: number;
	billDiscountPaise: number;
	/** Item discounts + bill discount. */
	discountPaise: number;
	serviceChargePaise: number;
	/**
	 * Value before tax: the sum of the tax groups' taxable values in the regular
	 * mode, otherwise subtotal − discount + service charge.
	 */
	taxablePaise: number;
	cgstPaise: number;
	sgstPaise: number;
	/** Between −50 and +50 when round-off is on, otherwise 0. */
	roundOffPaise: number;
	/** taxable + CGST + SGST + round-off. */
	totalPaise: number;
}

/** An offer applied on the bill: what it took off, item offers summed over their lines. */
export interface AppliedOffer {
	offerId: string;
	/** The offer's name, as printed. */
	label: string;
	scope: "item" | "bill";
	discountPaise: number;
}

export interface BillCalculation {
	gstMode: GstMode;
	pricesIncludeTax: boolean;
	roundOff: boolean;
	lines: BillLine[];
	billDiscount: AppliedDiscount | null;
	/** Null when the restaurant charges none. */
	serviceCharge: ServiceCharge | null;
	/** One row per tax rate, lowest rate first. Empty unless the GST mode is regular. */
	taxes: TaxGroup[];
	totals: BillTotals;
	/**
	 * The offers among the discounts (those with an `offerId`), item offers
	 * first in line order, then the bill offer. Recorded on the bill (P9-T04).
	 */
	offers: AppliedOffer[];
}

/** The offers among a calculation's discounts. */
export function appliedOffers(
	lines: readonly Pick<BillLine, "discount" | "itemDiscountPaise">[],
	billDiscount: AppliedDiscount | null,
): AppliedOffer[] {
	const byOffer = new Map<string, AppliedOffer>();
	for (const line of lines) {
		const offerId = line.discount?.offerId;
		if (!offerId || line.itemDiscountPaise === 0) continue;
		const seen = byOffer.get(offerId);
		if (seen) seen.discountPaise += line.itemDiscountPaise;
		else {
			byOffer.set(offerId, {
				offerId,
				label: line.discount?.label ?? "",
				scope: "item",
				discountPaise: line.itemDiscountPaise,
			});
		}
	}
	const result = [...byOffer.values()];
	if (billDiscount?.offerId && billDiscount.amountPaise > 0) {
		result.push({
			offerId: billDiscount.offerId,
			label: billDiscount.label ?? "",
			scope: "bill",
			discountPaise: billDiscount.amountPaise,
		});
	}
	return result;
}

export function calculateBill(input: BillInput): BillCalculation {
	assertBasisPoints(input.defaultTaxRateBp, "default tax rate");
	assertBasisPoints(input.serviceChargeBp, "service charge");
	const taxed = input.gstMode === "regular";

	// Steps 1 and 2: line gross, then the item discount.
	const afterItem = input.lines.map((line) => {
		checkLine(line);
		const grossPaise = lineGross(line.unitPricePaise, line.modifiersTotalPaise, line.quantity);
		const itemDiscountPaise = line.discount ? discountAmount(line.discount.rule, grossPaise) : 0;
		return { line, grossPaise, itemDiscountPaise, valuePaise: grossPaise - itemDiscountPaise };
	});

	// Step 3: the bill discount, spread in proportion to the value after item discounts.
	const afterItemTotal = sum(afterItem.map((l) => l.valuePaise));
	const billDiscountPaise = input.billDiscount
		? discountAmount(input.billDiscount.rule, afterItemTotal)
		: 0;
	const shares = allocate(
		billDiscountPaise,
		afterItem.map((l) => l.valuePaise),
	);

	const lines: BillLine[] = afterItem.map(({ line, grossPaise, itemDiscountPaise }, i) => {
		const share = shares[i] ?? 0;
		return {
			id: line.id,
			name: line.name,
			variantName: line.variantName,
			modifiers: line.modifiers.map((m) => ({ name: m.name, priceDeltaPaise: m.priceDeltaPaise })),
			unitPricePaise: line.unitPricePaise,
			modifiersTotalPaise: line.modifiersTotalPaise,
			quantity: line.quantity,
			taxRateBp: line.taxRateBp,
			grossPaise,
			discount: line.discount,
			itemDiscountPaise,
			billDiscountPaise: share,
			netPaise: grossPaise - itemDiscountPaise - share,
		};
	});

	// Step 4: service charge on the discounted subtotal.
	const discountedSubtotal = sum(lines.map((l) => l.netPaise));
	const serviceChargePaise = mulDivRound(discountedSubtotal, input.serviceChargeBp, 10000);
	const serviceCharge: ServiceCharge | null =
		input.serviceChargeBp > 0
			? {
					rateBp: input.serviceChargeBp,
					amountPaise: serviceChargePaise,
					taxRateBp: taxed ? input.defaultTaxRateBp : 0,
				}
			: null;

	// Step 5: tax per rate group. The service charge joins the default rate's group.
	const taxes: TaxGroup[] = [];
	if (taxed) {
		const valueByRate = new Map<number, number>();
		for (const l of lines)
			valueByRate.set(l.taxRateBp, (valueByRate.get(l.taxRateBp) ?? 0) + l.netPaise);
		if (serviceCharge) {
			const rate = input.defaultTaxRateBp;
			valueByRate.set(rate, (valueByRate.get(rate) ?? 0) + serviceChargePaise);
		}
		for (const rateBp of [...valueByRate.keys()].sort((a, b) => a - b)) {
			const value = valueByRate.get(rateBp) ?? 0;
			const tax = input.pricesIncludeTax
				? taxInInclusive(value, rateBp)
				: taxOnExclusive(value, rateBp);
			taxes.push({ rateBp, ...tax });
		}
	}

	const taxablePaise = taxed
		? sum(taxes.map((t) => t.taxablePaise))
		: discountedSubtotal + serviceChargePaise;
	const cgstPaise = sum(taxes.map((t) => t.cgstPaise));
	const sgstPaise = sum(taxes.map((t) => t.sgstPaise));

	// Step 6: round-off to the nearest rupee, half a rupee up.
	const beforeRoundOff = taxablePaise + cgstPaise + sgstPaise;
	const roundOffPaise = input.roundOff
		? mulDivRound(beforeRoundOff, 1, 100) * 100 - beforeRoundOff
		: 0;

	const itemDiscountPaise = sum(lines.map((l) => l.itemDiscountPaise));
	const billDiscount: AppliedDiscount | null = input.billDiscount
		? { ...input.billDiscount, amountPaise: billDiscountPaise }
		: null;
	return {
		gstMode: input.gstMode,
		pricesIncludeTax: input.pricesIncludeTax,
		roundOff: input.roundOff,
		lines,
		billDiscount,
		serviceCharge,
		taxes,
		totals: {
			subtotalPaise: sum(lines.map((l) => l.grossPaise)),
			itemDiscountPaise,
			billDiscountPaise,
			discountPaise: itemDiscountPaise + billDiscountPaise,
			serviceChargePaise,
			taxablePaise,
			cgstPaise,
			sgstPaise,
			roundOffPaise,
			// Step 7.
			totalPaise: taxablePaise + cgstPaise + sgstPaise + roundOffPaise,
		},
		offers: appliedOffers(lines, billDiscount),
	};
}

function checkLine(line: BillLineInput): void {
	assertPaise(line.unitPricePaise, `${line.name}: unit price`);
	assertInteger(line.modifiersTotalPaise, `${line.name}: modifiers total`);
	assertInteger(line.quantity, `${line.name}: quantity`);
	if (line.quantity < 1) throw new RangeError(`${line.name}: quantity must be at least 1`);
	assertBasisPoints(line.taxRateBp, `${line.name}: tax rate`);
	const deltas = sum(line.modifiers.map((m) => m.priceDeltaPaise));
	if (deltas !== line.modifiersTotalPaise) {
		throw new RangeError(`${line.name}: modifiers total ${line.modifiersTotalPaise} ≠ ${deltas}`);
	}
}
